import {BrowserModule} from '@angular/platform-browser';
import {APP_INITIALIZER, ErrorHandler, NgModule} from '@angular/core';
import {RouterModule} from '@angular/router';
import {LocationStrategy, PathLocationStrategy} from '@angular/common';
import {DataTablesModule} from 'angular-datatables';
import {HttpClientModule, HttpClient, HTTP_INTERCEPTORS} from '@angular/common/http';
import {TranslateModule, TranslateLoader} from '@ngx-translate/core';
import {TranslateHttpLoader} from '@ngx-translate/http-loader';
import * as Raven from 'raven-js';

// Datenfeld mit Routen importieren
import {ARR_ROUTES} from './app.routes';

// App modules/components
import {AppComponent} from './app.component';
import {LayoutsModule} from './layouts/layouts.module';
import {ContentsModule} from './modules/content/content.module';
import {NavigationService} from './services/navigation/navigation.service';
import {GlobalErrorHandler} from '../errorhandler.component';
import {AuthGuard} from './guards';
import {AuthService, UsersService } from './services';
import {FormsModule} from '@angular/forms';
import {SharedModule} from './shared.module';
import {TabsModule} from 'ngx-bootstrap/tabs';
import {TooltipModule} from 'ngx-bootstrap/tooltip';
import {TokenInterceptor} from './token.interceptor';
import {NavigationGuard} from './guards/navigation.guards';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {ToastrModule} from 'ngx-toastr';
import {ErrorGuard} from './guards/error.guards';
import {FrameworkModule} from './modules/framework/framework.module';
import {ENVIRONMENT} from '../environments/environment';
import {StartupService} from './services/startup/startup.service';
import {ConfigGuards} from './guards/config.guards';
import {HelperService} from "./services/helperService";
import {SharedDataService} from './services/shared/shared-data.service';
import {Ng4LoadingSpinnerModule} from "ng4-loading-spinner";
import {LayoutService} from "./services/layout.service";
import {DatatableEditableModule} from "./modules/datatable-editable/datatable-editable.module";
import {AccordionModule} from "ngx-bootstrap/accordion";
import {ModalModule} from "ngx-bootstrap/modal";
import {ImportsModule} from "./modules/imports/imports.module";

Raven.config(ENVIRONMENT.sentry_dns).install();

@NgModule
({
	// The declarations list identifies the application's only OBJ_component
	// Haupt-Komponente unserer Anwendung
	declarations: [
		AppComponent
	],
	// potentielle Abhaengigkeiten zu anderen Modulen
	imports: [
		BrowserModule,
		BrowserAnimationsModule,
		Ng4LoadingSpinnerModule.forRoot(),
		TooltipModule.forRoot(),
		ToastrModule.forRoot({
			timeOut: 3000,
			positionClass: 'toast-top-right',
			preventDuplicates: true,
			// progressBar: true,
			progressAnimation: 'increasing',
			closeButton: true,
			tapToDismiss: true,
			maxOpened: 4
		}),
		FormsModule,
		HttpClientModule,
		FrameworkModule,
        ContentsModule,
		ImportsModule,
		DatatableEditableModule,
		LayoutsModule,
        RouterModule.forRoot(
        	ARR_ROUTES,
			{
				enableTracing: false, // <-- debugging purposes only
        		onSameUrlNavigation: 'reload'
			}
		),
		TranslateModule.forRoot({
			loader: {
				provide: TranslateLoader,
				useFactory: (createTranslateLoader),
				deps: [HttpClient]
			}
		}),
        DataTablesModule,
        SharedModule.forRoot(),
		TabsModule.forRoot(),
		AccordionModule.forRoot(),
        ModalModule.forRoot()
    ],
	providers: [
		LayoutService,
		HelperService,
		{
			provide: HTTP_INTERCEPTORS,
			useClass: TokenInterceptor, // set global headers for API call
			multi: true
		},
        {
        	provide: 'Location',
			useValue: window.location
		},
		{
			provide: LocationStrategy,
			useClass: PathLocationStrategy
		},
        NavigationService,
		{
			provide: ErrorHandler,
			useClass: GlobalErrorHandler
		},
        AuthGuard,
        NavigationGuard,
		ErrorGuard,
		ConfigGuards,
        AuthService,
        UsersService,
        StartupService,
        {
            provide: APP_INITIALIZER,
            useFactory: (startupServiceFactory),
            deps: [StartupService],
            multi: true
        },
        SharedDataService
	],
	bootstrap: [AppComponent]
})
export class AppModule {} // end class AppModule


/**
 * Function > createTranslateLoader
 * @param {HttpClient} OBJ_http_client
 * @returns {TranslateHttpLoader}
 */
export function createTranslateLoader(OBJ_http_client: HttpClient)
{
	// die Abkuerzung i18n ist ein Numeronym und steht fuer Internationalization
	return new TranslateHttpLoader(OBJ_http_client, 'assets/i18n/', '.json');
} // end function createTranslateLoader


/**
 * Function > startupServiceFactory
 * @param {StartupService} OBJ_startup_service
 * @return {Function}
 */
export function startupServiceFactory(OBJ_startup_service: StartupService): Function {

    console.log
	(
		{class: 'AppModule', function: 'startupServiceFactory'},
		'config_path: ' + ENVIRONMENT.config_path
	);

    return () => OBJ_startup_service.load(ENVIRONMENT.config_path);

} // end function startupServiceFactory
